import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SiteLayout } from '@/components/layout/SiteLayout';
import { OnlineMenuLayout } from '@/components/onlineMenu/OnlineMenuLayout';
import { MenuPreviewLayout } from '@/components/printMenu/MenuPreviewLayout';
import ComingSoonPage from '@/pages/ComingSoonPage';
import Home from '@/pages/Home';
import NotFound from '@/pages/NotFound';
import PrintMenuPage from '@/pages/PrintMenuPage';
import { isComingSoonMode } from '@/lib/comingSoon';

const FAQ = lazy(() => import('@/pages/FAQ'));
const Location = lazy(() => import('@/pages/Location'));
const Gallery = lazy(() => import('@/pages/Gallery'));
const OnlineMenuPage = lazy(() => import('@/pages/OnlineMenuPage'));
const BookingPage = lazy(() =>
  import('@/pages/BookingPage').then((module) => ({ default: module.BookingPage })),
);
const BookingCompletePage = lazy(() =>
  import('@/pages/BookingCompletePage').then((module) => ({
    default: module.BookingCompletePage,
  })),
);
const GiftCardsPage = lazy(() =>
  import('@/pages/GiftCardsPage').then((module) => ({ default: module.GiftCardsPage })),
);
const GiftCardCompletePage = lazy(() =>
  import('@/pages/GiftCardCompletePage').then((module) => ({
    default: module.GiftCardCompletePage,
  })),
);

function PageFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center font-medium text-muted">
      Loading…
    </div>
  );
}

function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

export default function App() {
  if (isComingSoonMode()) {
    return (
      <Routes>
        <Route path="*" element={<ComingSoonPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* In-salon QR menu */}
      <Route path="menu" element={<OnlineMenuLayout />}>
        <Route
          index
          element={
            <LazyPage>
              <OnlineMenuPage />
            </LazyPage>
          }
        />
      </Route>

      {/* Print menu booklet preview (internal / design) */}
      <Route path="menu2" element={<MenuPreviewLayout />}>
        <Route index element={<PrintMenuPage />} />
      </Route>

      {/* Marketing site routes (with site layout) */}
      <Route element={<SiteLayout />}>
        <Route index element={<Home />} />
        <Route
          path="booking"
          element={
            <LazyPage>
              <BookingPage />
            </LazyPage>
          }
        />
        <Route
          path="booking/complete"
          element={
            <LazyPage>
              <BookingCompletePage />
            </LazyPage>
          }
        />
        <Route
          path="gift-cards"
          element={
            <LazyPage>
              <GiftCardsPage />
            </LazyPage>
          }
        />
        <Route
          path="gift-cards/complete"
          element={
            <LazyPage>
              <GiftCardCompletePage />
            </LazyPage>
          }
        />
        <Route path="services" element={<Navigate to="/booking" replace />} />
        <Route
          path="gallery"
          element={
            <LazyPage>
              <Gallery />
            </LazyPage>
          }
        />
        <Route
          path="faq"
          element={
            <LazyPage>
              <FAQ />
            </LazyPage>
          }
        />
        <Route
          path="location"
          element={
            <LazyPage>
              <Location />
            </LazyPage>
          }
        />
        <Route path="policies" element={<Navigate to="/faq" replace />} />
        <Route path="contact" element={<Navigate to="/location#contact" replace />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
